/**
 * predict.ts — client-side prediction for the LOCAL player only.
 *
 * The server is still the only authority on anything that matters (AGENTS.md
 * server-authoritative): this module sends nothing, decides no outcomes, and does no
 * combat. All it does is answer one question — "given the commands I have sent
 * that the server has not confirmed yet, where would my own player be?" — so the
 * view can show your own movement, your own guard and your own swing on the
 * frame you asked for them instead of a round trip later.
 *
 * The loop is the standard one:
 *
 *   1. every command is stepped locally the moment it is sent, and kept;
 *   2. every snapshot carries `ack`, the newest command the server had applied;
 *   3. on each snapshot the client throws away acked commands, re-simulates the
 *      remainder from the authoritative state, and blends out the difference
 *      between where it *was* drawing you and where the replay says you are.
 *
 * Step 3 is what keeps prediction honest. The predicted state is never trusted
 * across a snapshot: it is rebuilt from server truth every tick, so a mispredict
 * costs a small smoothed correction and nothing accumulates.
 *
 * Two things are deliberately NOT predicted:
 *
 * - **Anyone else.** Opponents stay interpolated behind INTERP_DELAY_MS. Guessing
 *   at an opponent's motion would move the thing you are timing a parry against.
 * - **Combat outcomes.** A predicted swing is a picture of your own blade. Hits,
 *   blocks, parries and damage arrive only as server events, so a predicted swing
 *   that the server refuses simply vanishes and never took anything with it.
 *
 * The one collision model the client has is the arena box (`clampToArena`), since
 * Rapier is server-side only. Walking into another player is therefore a
 * mispredict, and a deliberate one: it is rare, it is small, and reconciliation
 * already knows how to absorb it.
 */

import {
  ACTION,
  EMOTE_IDS,
  FEEL,
  actionClip,
  actionMoveMult,
  actionTurnCap,
  advanceStridePhase,
  attackPhase,
  loadoutMoveMult,
  strideFor,
  unpackLoadout,
  clampDialAngle,
  clampToSwingSide,
  isMirroredDial,
  clampToArena,
  clipDurationMs,
  clipProgress,
  chainEntryMs,
  inBufferWindow,
  canCombo,
  localPoseAt,
  slewDialAngle,
  wrapAngle,
  TRAJECTORIES,
  stepLocomotion,
  stepLocomotionAnim,
  tickMs,
  type ActionCode,
  type AnimState,
  type AnimStateId,
  type ClipId,
  type InputCommand,
  type LocomotionState,
  type Loadout,
  type PlayerSnapshot,
  type Snapshot,
  type Vec3,
} from '@dueler/shared';

/** What the renderer needs to draw the local player. */
export interface PredictedView {
  /** Capsule centre, with the reconciliation error blended in. */
  pos: Vec3;
  yaw: number;
  anim: AnimState;
  blocking: boolean;
  pitch: number;
  /** The predicted walk cycle, so your own legs answer the key, not the wire. */
  gaitPhase: number;
  gaitStride: number;
  /** Combo blend out of the previous cut — local prediction only. */
  chainFrom?: { hilt: [number, number, number]; tip: [number, number, number] } | undefined;
  chainBlendMs?: number | undefined;
}

export interface Predictor {
  /**
   * Steps the prediction by one tick with a command that is being sent right now,
   * and remembers it for replay. Call this at send time, not on receipt: the
   * whole point is that the local view does not wait for the network.
   */
  record(command: InputCommand, now: number): void;
  /** Folds an authoritative snapshot in: drop acked commands, replay the rest. */
  reconcile(snapshot: Snapshot, selfSlot: number, now: number): void;
  /**
   * Opens the local riposte window after the server reports you blocked/parried.
   * Combat events are not on the snapshot, so the room calls this explicitly.
   */
  openRiposteWindow(): void;
  /**
   * The local player as it should be drawn, or undefined when prediction has
   * nothing to stand on (before the first snapshot, while dead, or after the
   * connection has stopped acking) — in which case the caller falls back to plain
   * interpolation.
   */
  view(now: number): PredictedView | undefined;
  /** Commands sent but not yet confirmed. The F3 readout shows this. */
  /** Whether a swing is in flight locally, without waiting for a snapshot. */
  readonly attacking: boolean;
  readonly pending: number;
  /**
   * Magnitude of the correction the MOST RECENT snapshot asked for, m — how far
   * prediction and server had drifted apart when they were last compared, not a
   * live countdown of what is left to blend. It refreshes every snapshot, which
   * makes it the useful number for the readout: a steady few centimetres is
   * prediction working, and a large or growing figure is prediction fighting the
   * server.
   */
  readonly correction: number;
  reset(): void;
}

/**
 * Ticks of unconfirmed input to tolerate before giving up and handing the local
 * player back to interpolation. Three seconds: past that the connection is not
 * lagging, it is gone, and continuing to predict would walk a ghost across the
 * arena.
 */
const MAX_PENDING = FEEL.TICK_HZ * 3;

/** Matches movementSystem's easing of the stride amplitude, s. */
const GAIT_SMOOTH_S = 0.09;

/**
 * Clips the server owns outright; prediction has no opinion on them.
 *
 * Emotes are in here with the reactions because they decide nothing. Predicting
 * one would buy a few tens of milliseconds on a bow, at the cost of a second
 * place that has to agree about when an emote starts, ends and is abandoned.
 */
const REACTION_IDS: ReadonlySet<AnimStateId> = new Set<AnimStateId>([
  'hit',
  'death',
  ...EMOTE_IDS,
]);

const ATTACK_IDS: ReadonlySet<AnimStateId> = new Set<AnimStateId>(['slash', 'stab']);

const isAttackId = (id: AnimStateId): id is ClipId => ATTACK_IDS.has(id);

/** A swing in flight, in the same clip clock the server keeps. */
interface Swing {
  clipId: ClipId;
  angle: number;
  /** Dial this chain is rolling toward. Equals angle on fresh swings. */
  aimAngle: number;
  /** True when started from a recovery follow-up. */
  chained: boolean;
  /** True when started inside the post-block/parry riposte window. */
  riposte: boolean;
  /** Root-space blade we are blending out of during a combo windup. */
  chainFrom?: { hilt: [number, number, number]; tip: [number, number, number] } | undefined;
  chainBlendMs: number;
  /** The side it is held out on, latched at the start. Mirrors the server. */
  mirrored: boolean;
  /**
   * Clip time in ms with charge time removed — exactly what the server's
   * `attackElapsedMs` produces, so `attackPhase` and `clipProgress` mean the same
   * thing on both ends.
   */
  elapsedMs: number;
  heldMs: number;
  held: boolean;
}

/** Everything the local simulation carries from one tick to the next. */
interface SimState {
  loco: LocomotionState;
  anim: AnimState;
  swing: Swing | undefined;
  blocking: boolean;
  /** The dial the guard was raised on. Latched for as long as it is held. */
  guardDial: number;
  /** An action parked during the recovery buffer window, as combatSystem does. */
  buffered: ActionCode;
  bufferedAgeMs: number;
  /** ms remaining of the post-defend riposte window. */
  riposteRemainingMs: number;
  /**
   * What the player is wearing, straight off the snapshot.
   *
   * Authoritative and never touched by a replay — it is here rather than read
   * from a closure because a replay must reproduce the server's arithmetic
   * exactly, and its weight is one of the inputs to that arithmetic.
   */
  kit: Loadout;
  /**
   * The walk cycle. Predicted like everything else about your own body, because
   * your legs are hitboxes and they must not lag a round trip behind your feet.
   */
  gaitPhase: number;
  gaitStride: number;
}

const zero = (): Vec3 => ({ x: 0, y: 0, z: 0 });

/**
 * One tick of local simulation, in the server's pipeline order:
 * statusSystem (effective stats) → movementSystem (yaw, motion, locomotion clip)
 * → combatSystem (start / hold / retire the swing, then the guard).
 *
 * The order matters and is not cosmetic: because combat resolves actions AFTER
 * movement, the tick a swing starts on still moves at full speed. Reordering
 * these would make the first tick of every attack drift.
 *
 * Stagger and any other modifier are invisible here — they live in the server's
 * modifier list and never reach the client — so a staggered player over-predicts
 * their own speed for one round trip and is corrected. That is the intended
 * trade: modifiers are rare, movement is constant.
 */
function step(state: SimState, cmd: InputCommand, dt: number): SimState {
  const attacking = state.swing !== undefined;
  const inActive =
    state.swing !== undefined &&
    attackPhase(state.swing.clipId, state.swing.elapsedMs) === 'active';

  // The same two multipliers statusSystem applies, in the same order: what you
  // are doing, then what you are carrying. A second opinion about either is a
  // permanent mispredict that reconciliation would spend every tick fighting
  // (AGENTS.md: the movement step lives in shared).
  const moveSpeed =
    FEEL.MOVE_SPEED *
    actionMoveMult({ attacking, blocking: state.blocking }) *
    loadoutMoveMult(state.kit);
  const turnCap = actionTurnCap(FEEL.TURN_CAP_IDLE, inActive);

  // ---- movement ----
  const stepped = stepLocomotion(state.loco, cmd, { moveSpeed, turnCap }, dt);
  const pos = clampToArena(stepped.pos);
  // Velocity is what actually happened, not what was asked for — the same rule
  // the server applies after the character controller has had its say. Running at
  // a wall really is zero speed, and the locomotion cycle reads this number.
  const loco: LocomotionState = {
    pos,
    yaw: stepped.yaw,
    vel: { x: (pos.x - state.loco.pos.x) / dt, y: 0, z: (pos.z - state.loco.pos.z) / dt },
  };

  // ---- the walk ----
  //
  // The same two lines movementSystem runs, on the same settled motion, so a
  // predicted shin is where the server will say it is.
  const gaitPhase = advanceStridePhase(
    state.gaitPhase,
    pos.x - state.loco.pos.x,
    pos.z - state.loco.pos.z,
    loco.yaw,
  );
  const gaitStride =
    state.gaitStride +
    (strideFor(Math.hypot(loco.vel.x, loco.vel.z)) - state.gaitStride) *
      (1 - Math.exp(-dt / GAIT_SMOOTH_S));

  // ---- the swing ----
  const tick = tickMs();
  let swing = state.swing === undefined ? undefined : { ...state.swing };
  let buffered = state.buffered;
  let bufferedAgeMs = state.bufferedAgeMs + tick;
  let riposteRemainingMs = Math.max(0, state.riposteRemainingMs - tick);
  const requested = cmd.a;
  let chainSource:
    | { clipId: ClipId; angle: number; mirrored: boolean; elapsedMs: number }
    | undefined;

  if (swing !== undefined) {
    // The tick advanced. A held tick is then subtracted straight back out, which
    // is what parks the swing at the top of its windup and sweeps nothing.
    swing.elapsedMs += tick;
    // Parks on the WOUND BACK pose, matching the server's resolveAction.
    const reachedTop =
      swing.elapsedMs >= FEEL.CLIPS[swing.clipId].windupMs * FEEL.WIND_BACK_AT;
    // No charge timer: the swing waits for the button, however long that is.
    swing.held = cmd.c && reachedTop;
    if (swing.held) {
      swing.heldMs += tick;
      swing.elapsedMs -= tick;
    }
    // Fresh swings snap-aim while held; combos roll freely — including across
    // the body for opposite-side follow-ups.
    if (swing.clipId === 'slash') {
      const phase = attackPhase(swing.clipId, swing.elapsedMs);
      if (cmd.c) {
        if (swing.chained) {
          swing.aimAngle = clampDialAngle(cmd.ang);
          swing.mirrored = isMirroredDial(swing.aimAngle);
          swing.angle = slewDialAngle(
            swing.angle,
            swing.aimAngle,
            (FEEL.CHAIN_ANGLE_SLEW * tick) / 1000,
          );
        } else {
          const target = clampToSwingSide(cmd.ang, swing.mirrored);
          swing.angle = target;
          swing.aimAngle = target;
        }
      } else if (swing.chained && phase === 'windup') {
        swing.angle = slewDialAngle(
          swing.angle,
          swing.aimAngle,
          (FEEL.CHAIN_ANGLE_SLEW * tick) / 1000,
        );
      }
    }
    if (swing.chainBlendMs > 0) {
      swing.chainBlendMs = Math.max(0, swing.chainBlendMs - tick);
      if (swing.chainBlendMs === 0) swing.chainFrom = undefined;
    }
  }

  const capture = (
    s: Swing,
  ): { clipId: ClipId; angle: number; mirrored: boolean; elapsedMs: number } => ({
    clipId: s.clipId,
    angle: s.angle,
    mirrored: s.mirrored,
    elapsedMs: s.elapsedMs,
  });

  const start = (action: ActionCode, chained: boolean): Swing | undefined => {
    const clipId = actionClip(action);
    if (clipId === undefined) return undefined;

    const isRiposte = riposteRemainingMs > 0;
    if (isRiposte) riposteRemainingMs = 0;

    // Combos are slash→slash only. Stabs always start from rest after full recovery.
    const mayChain =
      !isRiposte &&
      chained &&
      chainSource !== undefined &&
      canCombo(chainSource.clipId) &&
      canCombo(clipId);

    const blendFrom = (
      from: { clipId: ClipId; angle: number; elapsedMs: number },
    ): Pick<Swing, 'chainFrom' | 'chainBlendMs'> => {
      const prev = localPoseAt(
        TRAJECTORIES[from.clipId],
        Math.min(from.elapsedMs, clipDurationMs(from.clipId) - 1),
        from.angle,
      );
      return {
        chainFrom: {
          hilt: [prev.hilt[0]!, prev.hilt[1]!, prev.hilt[2]!],
          tip: [prev.tip[0]!, prev.tip[1]!, prev.tip[2]!],
        },
        chainBlendMs: FEEL.CHAIN_BLEND_MS,
      };
    };

    if (mayChain && chainSource !== undefined) {
      const angle = chainSource.angle;
      // Free dial on combos — opposite-side (right→left) must be reachable.
      const aimAngle = clampDialAngle(cmd.ang);
      return {
        clipId,
        angle,
        aimAngle,
        chained: true,
        riposte: false,
        mirrored: isMirroredDial(aimAngle),
        elapsedMs: chainEntryMs(clipId, wrapAngle(aimAngle - angle)),
        heldMs: 0,
        held: false,
        ...blendFrom(chainSource),
      };
    }

    const angle = clipId === 'slash' ? clampDialAngle(cmd.ang) : 0;
    const entryMs = isRiposte ? FEEL.CLIPS[clipId].windupMs * FEEL.RIPOSTE_ENTRY_AT : 0;
    return {
      clipId,
      angle,
      aimAngle: angle,
      chained: false,
      riposte: isRiposte,
      mirrored: isMirroredDial(angle),
      elapsedMs: entryMs,
      heldMs: 0,
      held: false,
      chainBlendMs: 0,
    };
  };

  if (requested === ACTION.Feint) {
    // Cancellable while held, and up to FEINT_BEFORE of the way through the
    // clip once released. Same rule as the server's canFeint. Ripostes never.
    if (
      swing !== undefined &&
      !swing.riposte &&
      (swing.held || swing.elapsedMs < clipDurationMs(swing.clipId) * FEEL.FEINT_BEFORE)
    ) {
      swing = undefined;
    }
  } else if (swing !== undefined) {
    const phase = attackPhase(swing.clipId, swing.elapsedMs);
    const inWindow = inBufferWindow(swing.clipId, swing.elapsedMs);

    if (requested !== ACTION.None && inWindow) {
      const nextClip = actionClip(requested);
      if (nextClip !== undefined && !canCombo(nextClip)) {
        buffered = requested;
        bufferedAgeMs = 0;
      } else if (phase === 'recovery' || phase === 'done') {
        chainSource = capture(swing);
        swing = start(requested, true);
      } else {
        buffered = requested;
        bufferedAgeMs = 0;
      }
    }

    if (swing !== undefined && buffered !== ACTION.None) {
      const livePhase = attackPhase(swing.clipId, swing.elapsedMs);
      const nextClip = actionClip(buffered);
      const wantsCombo = nextClip !== undefined && canCombo(nextClip);
      const ready = wantsCombo
        ? (livePhase === 'recovery' || livePhase === 'done') && bufferedAgeMs <= FEEL.BUFFER_MS
        : livePhase === 'done' && bufferedAgeMs <= FEEL.BUFFER_MS;
      if (ready) {
        const action = buffered;
        buffered = ACTION.None;
        chainSource = capture(swing);
        swing = start(action, wantsCombo);
      }
    }

    if (swing !== undefined && attackPhase(swing.clipId, swing.elapsedMs) === 'done') {
      chainSource = capture(swing);
      swing = undefined;
    }
  }

  if (swing === undefined && requested !== ACTION.Feint) {
    if (buffered !== ACTION.None) {
      const waiting = buffered;
      buffered = ACTION.None;
      const nextClip = actionClip(waiting);
      const wantsCombo = nextClip !== undefined && canCombo(nextClip);
      if (bufferedAgeMs <= FEEL.BUFFER_MS) swing = start(waiting, wantsCombo);
    }
    if (swing === undefined && requested !== ACTION.None) {
      const nextClip = actionClip(requested);
      const wantsCombo =
        nextClip !== undefined &&
        canCombo(nextClip) &&
        chainSource !== undefined &&
        canCombo(chainSource.clipId);
      swing = start(requested, wantsCombo);
    }
  }

  // Mordhau: cancel a riposte windup into a parry.
  if (
    cmd.b &&
    swing?.riposte === true &&
    attackPhase(swing.clipId, swing.elapsedMs) === 'windup'
  ) {
    swing = undefined;
  }

  // Committing to a swing drops the guard; blocking and attacking are exclusive.
  const blocking = cmd.b && swing === undefined;
  // The stance is latched when the guard goes up and held until it comes down,
  // exactly as the server latches it. Reading the live dial here would slide the
  // predicted guard between stances while the server's collider stayed put.
  const guardDial = blocking ? (state.blocking ? state.guardDial : cmd.ang) : 0;

  // ---- what to draw ----
  let anim: AnimState;
  if (swing !== undefined) {
    anim = {
      id: swing.clipId,
      t: clipProgress(swing.clipId, swing.elapsedMs),
      angle: swing.angle,
    };
  } else {
    const speed = Math.hypot(loco.vel.x, loco.vel.z);
    const next = stepLocomotionAnim(state.anim, speed, dt);
    // While guarding, `angle` carries the dial that aims the guard — the same
    // convention the snapshot uses, so the predicted guard and the server's
    // collider are posed from the same number.
    anim = { id: next.id, t: next.t, angle: guardDial };
  }

  // `kit` is carried through untouched: a replay reproduces the server's
  // arithmetic, and nothing a player does mid-tick changes what they are wearing.
  return {
    loco,
    anim,
    swing,
    blocking,
    guardDial,
    buffered,
    bufferedAgeMs,
    riposteRemainingMs,
    kit: state.kit,
    gaitPhase,
    gaitStride,
  };
}

export function createPredictor(): Predictor {
  /** The predicted state, rebuilt from server truth on every snapshot. */
  let state: SimState | undefined;
  /** Commands sent and not yet acked, oldest first. */
  let pending: InputCommand[] = [];
  /** Newest command recorded, for the fields the view takes straight from input. */
  let last: InputCommand | undefined;
  /** Server anim for the local player, so reaction clips still play. */
  let serverAnim: AnimState | undefined;

  /** Position/facing error being blended out, and when it was last accumulated. */
  let error = zero();
  let errorYaw = 0;
  let errorAt = 0;

  /** Local clock time the predicted state was last stepped, for sub-tick smoothing. */
  let steppedAt = 0;
  /** Previous authoritative sample, to recover the server's velocity by difference. */
  let lastAuth: { pos: Vec3; tick: number } | undefined;

  const dt = 1 / FEEL.TICK_HZ;

  /** The error left after decaying from `errorAt` to `now`. Pure: view may run twice. */
  const decayed = (now: number): { pos: Vec3; yaw: number } => {
    const k = Math.exp(-Math.max(0, now - errorAt) / FEEL.RECONCILE_MS);
    return { pos: { x: error.x * k, y: error.y * k, z: error.z * k }, yaw: errorYaw * k };
  };

  /**
   * Where the local player is being DRAWN at `now`: the predicted state, carried
   * across the gap between 60Hz commands, plus whatever error is still blending
   * out.
   *
   * `reconcile` and `view` must both go through this. Measuring the correction
   * against anything less than the fully composed position leaves the sub-tick
   * term out of the error, and the picture then jumps by up to one tick of motion
   * on every single snapshot — sixty times a second, which reads as jitter rather
   * than as the smoothing it is supposed to be.
   */
  const shown = (from: SimState, now: number): { pos: Vec3; yaw: number } => {
    const blend = decayed(now);
    // Bounded to one tick: this carries the state across a known gap, it does not
    // extrapolate into a guess.
    const ahead = Math.min(Math.max(now - steppedAt, 0), tickMs()) * 0.001;
    return {
      pos: {
        x: from.loco.pos.x + from.loco.vel.x * ahead + blend.pos.x,
        y: from.loco.pos.y + blend.pos.y,
        z: from.loco.pos.z + from.loco.vel.z * ahead + blend.pos.z,
      },
      yaw: from.loco.yaw + blend.yaw,
    };
  };

  const suspend = (): void => {
    state = undefined;
    pending = [];
    error = zero();
    errorYaw = 0;
    lastAuth = undefined;
  };

  /**
   * The authoritative velocity, differenced from consecutive snapshots. The
   * snapshot does not carry velocity, and starting a replay from zero would make
   * every correction re-run the acceleration ramp. Clamped to the top speed so
   * codec quantization noise cannot invent a sprint.
   */
  const authVelocity = (self: PlayerSnapshot, tick: number): Vec3 => {
    if (lastAuth === undefined || tick <= lastAuth.tick) return zero();
    const span = (tick - lastAuth.tick) * tickMs() * 0.001;
    const vx = (self.x - lastAuth.pos.x) / span;
    const vz = (self.z - lastAuth.pos.z) / span;
    const speed = Math.hypot(vx, vz);
    if (speed <= FEEL.MOVE_SPEED || speed < 1e-9) return { x: vx, y: 0, z: vz };
    const scale = FEEL.MOVE_SPEED / speed;
    return { x: vx * scale, y: 0, z: vz * scale };
  };

  /**
   * The swing the SERVER says is in flight, as a replay base.
   *
   * Reconstructed from the snapshot's animation state rather than carried over
   * locally, and that is the important part: it makes the server the authority on
   * whether a swing exists at all and where it is in its clip. A swing the server
   * refused — because the attacker was staggered, or already busy — is simply
   * absent here and disappears from the prediction on the next snapshot. A swing
   * the client did not predict appears the same way.
   *
   * `t` is normalized clip time, so the clock is recoverable exactly.
   */
  const authSwing = (self: PlayerSnapshot, prev: Swing | undefined): Swing | undefined => {
    if (!isAttackId(self.anim.id)) return undefined;
    return {
      clipId: self.anim.id,
      angle: self.anim.angle,
      // Chain aim / blend are not on the wire; keep the local ones through
      // reconcile so a mid-combo prediction does not snap.
      aimAngle: prev?.chained ? prev.aimAngle : self.anim.angle,
      chained: prev?.chained ?? false,
      riposte: prev?.riposte ?? false,
      chainFrom: prev?.chainFrom,
      chainBlendMs: prev?.chainBlendMs ?? 0,
      mirrored: prev?.chained ? prev.mirrored : isMirroredDial(self.anim.angle),
      elapsedMs: self.anim.t * clipDurationMs(self.anim.id),
      heldMs: prev?.heldMs ?? 0,
      held: false,
    };
  };

  return {
    openRiposteWindow() {
      if (state === undefined) return;
      state = { ...state, riposteRemainingMs: FEEL.RIPOSTE_WINDOW_MS };
    },

    record(command, now) {
      last = command;
      if (state === undefined) return;
      if (pending.length >= MAX_PENDING) {
        // Nothing has been acked for seconds. Predicting further is fiction.
        suspend();
        return;
      }
      state = step(state, command, dt);
      pending.push(command);
      steppedAt = now;
    },

    reconcile(snapshot, selfSlot, now) {
      const self = snapshot.players.find((p) => p.id === selfSlot);
      // Not spawned yet, or dead: the dead do not move and their clip belongs to
      // the server, so hand the local player back to plain interpolation.
      if (self === undefined || self.health <= 0) {
        suspend();
        serverAnim = self?.anim;
        return;
      }
      serverAnim = self.anim;

      const authPos: Vec3 = { x: self.x, y: self.y, z: self.z };
      const authoritative: LocomotionState = {
        pos: authPos,
        yaw: self.yaw,
        vel: authVelocity(self, snapshot.tick),
      };
      lastAuth = { pos: authPos, tick: snapshot.tick };

      // First snapshot, or coming back from a suspend: adopt server truth whole.
      if (state === undefined) {
        state = {
          loco: authoritative,
          anim: { ...self.anim },
          swing: authSwing(self, undefined),
          blocking: self.blocking,
          guardDial: self.blocking ? self.anim.angle : 0,
          buffered: ACTION.None,
          bufferedAgeMs: 0,
          riposteRemainingMs: 0,
          kit: unpackLoadout(self.kit),
          gaitPhase: self.gaitPhase,
          gaitStride: self.gaitStride,
        };
        pending = [];
        error = zero();
        errorYaw = 0;
        errorAt = now;
        steppedAt = now;
        return;
      }

      // Where we are currently DRAWING the player. The correction is measured
      // against this, not against the raw predicted state, so the accumulated
      // error is never counted twice and the picture never jumps.
      const before = shown(state, now);

      // Anything the server has applied is history. A command it dropped —
      // two arriving inside one tick, latest-wins — is acked past and disappears
      // here too, which is exactly right: the extra local step it caused is
      // discarded by replaying only what is left.
      pending = pending.filter((c) => c.seq > self.ack);

      let replayed: SimState = {
        loco: authoritative,
        anim: { ...self.anim },
        swing: authSwing(self, state.swing),
        blocking: self.blocking,
        // The server's latched dial rides in on the snapshot, so replay resumes
        // from the stance it actually chose.
        guardDial: self.blocking ? self.anim.angle : 0,
        // The server's buffered intent is not on the wire; carry the local one —
        // except through a reaction, which clears it server-side. Carrying it
        // would replay straight into a new swing and undo the interrupt.
        buffered: REACTION_IDS.has(self.anim.id) ? ACTION.None : state.buffered,
        bufferedAgeMs: state.bufferedAgeMs,
        // Riposte window is opened by combat events, not the snapshot — keep it.
        riposteRemainingMs: state.riposteRemainingMs,
        // Re-read every snapshot rather than carried: the wardrobe change goes
        // up as a message and comes back down here, so this is the moment the
        // predictor learns it is now wearing plate.
        kit: unpackLoadout(self.kit),
        // Replayed forward from server truth like the position is.
        gaitPhase: self.gaitPhase,
        gaitStride: self.gaitStride,
      };
      for (const command of pending) replayed = step(replayed, command, dt);
      state = replayed;
      steppedAt = now;

      const drift = Math.hypot(
        before.pos.x - replayed.loco.pos.x,
        before.pos.y - replayed.loco.pos.y,
        before.pos.z - replayed.loco.pos.z,
      );
      if (drift > FEEL.RECONCILE_SNAP_M) {
        // A respawn or a hard knockback is a real teleport. Gliding a metre and a
        // half to catch up would be a lie about where you are standing.
        error = zero();
        errorYaw = 0;
      } else {
        error = {
          x: before.pos.x - replayed.loco.pos.x,
          y: before.pos.y - replayed.loco.pos.y,
          z: before.pos.z - replayed.loco.pos.z,
        };
        errorYaw = wrapAngle(before.yaw - replayed.loco.yaw);
      }
      errorAt = now;
    },

    view(now) {
      if (state === undefined || last === undefined) return undefined;
      const drawn = shown(state, now);

      // A reaction clip is the server's to play. Nothing is predicted over the
      // top of it any more: getting hit cancels your swing, so by the time the
      // server reports 'hit' the predicted swing is already gone — `authSwing`
      // returns nothing for a reaction id, and the replay starts from that.
      const anim =
        state.swing === undefined && serverAnim !== undefined && REACTION_IDS.has(serverAnim.id)
          ? serverAnim
          : state.anim;

      return {
        pos: drawn.pos,
        yaw: wrapAngle(drawn.yaw),
        anim,
        blocking: state.blocking,
        // Straight off the newest command: the guard tracks the mouse with no
        // network in the way at all.
        pitch: last.pitch,
        gaitPhase: state.gaitPhase,
        gaitStride: state.gaitStride,
        chainFrom: state.swing?.chainFrom,
        chainBlendMs: state.swing?.chainBlendMs,
      };
    },

    get attacking() {
      return state?.swing !== undefined;
    },

    get pending() {
      return pending.length;
    },

    get correction() {
      return Math.hypot(error.x, error.y, error.z);
    },

    reset() {
      suspend();
      last = undefined;
      serverAnim = undefined;
    },
  };
}
